import { useTranslation } from "react-i18next";

const SECTIONS_EN = [
  {
    title: "1. What are cookies?",
    body: `Cookies are small text files that a website stores on your device (computer, tablet, or phone) when you visit it. They are widely used to make websites work, to work more efficiently, and to provide information to the site operator.

Alongside cookies, similar technologies such as local storage (localStorage) and session storage may be used to store information in your browser. In this Policy, "cookies" refers to all such technologies.`,
  },
  {
    title: "2. How Profio uses cookies",
    body: `Profio (profioplatform.com) uses cookies and local storage to:
(a) keep you signed in and maintain your session (authentication via Firebase);
(b) remember your language preference (Ukrainian or English) and other interface settings;
(c) remember your cookie choice so we do not ask again on every visit;
(d) keep the Platform secure and detect abuse;
(e) understand, in aggregate and anonymised form, how the Platform is used so we can improve it.`,
  },
  {
    title: "3. Categories of cookies we use",
    body: `Strictly necessary cookies. Required for the Platform to function — for example, to sign you in, keep your session active, and remember your consent choice. These cannot be switched off through the banner because the Platform would not work without them.

Preference cookies. Remember choices you make, such as your language, to give you a more personalised experience.

Analytics cookies. Help us understand how visitors interact with the Platform by collecting information in an aggregated, anonymised form. These are only set if you accept them.

We do not use cookies to sell your personal data or to serve third-party advertising.`,
  },
  {
    title: "4. Third-party cookies",
    body: `Some cookies may be set by third-party services we rely on to operate the Platform, including:
(a) Firebase (Google) — authentication and session management;
(b) our payment providers — when you top up your Wallet or make a payment;
(c) Telegram — only if you interact with our verification bot.

These providers process data in accordance with their own privacy policies, over which Profio has no control.`,
  },
  {
    title: "5. Your choices and how to manage cookies",
    body: `When you first visit the Platform, a banner lets you accept all cookies or reject non-essential ones. Strictly necessary cookies are always active because the Platform cannot function without them.

You can also control cookies through your browser settings — most browsers let you view, delete, and block cookies. Note that if you block strictly necessary cookies, parts of the Platform (such as signing in) may stop working.

To change a choice you have already made, clear the "profio:cookie-consent" value in your browser storage (or your site data for profioplatform.com) and reload the page; the banner will appear again.`,
  },
  {
    title: "6. How long cookies are stored",
    body: `Session cookies are deleted when you close your browser. Persistent cookies (such as your language and consent choice) remain until they expire or until you delete them. Consent choices are kept so that we do not repeatedly ask you on each visit.`,
  },
  {
    title: "7. Changes to this Policy",
    body: `We may update this Cookie Policy from time to time to reflect changes in the technologies we use or in applicable law. The current version is always available at profioplatform.com/cookies, and the "last updated" date below indicates when it was last revised.`,
  },
  {
    title: "8. Contact",
    body: `If you have questions about this Cookie Policy or how we use cookies, contact us at support@profioplatform.com. For information on how we process personal data more generally, see our Privacy Policy at profioplatform.com/privacy.`,
  },
];

const SECTIONS_UK = [
  {
    title: "1. Що таке файли cookie?",
    body: `Файли cookie — це невеликі текстові файли, які вебсайт зберігає на вашому пристрої (комп'ютері, планшеті чи телефоні) під час його відвідування. Вони широко використовуються для того, щоб сайти працювали, працювали ефективніше та надавали інформацію оператору сайту.

Разом із cookie можуть застосовуватися подібні технології, зокрема локальне сховище (localStorage) та сесійне сховище, що зберігають інформацію у вашому браузері. У цій Політиці термін «cookie» охоплює всі такі технології.`,
  },
  {
    title: "2. Як Profio використовує cookie",
    body: `Profio (profioplatform.com) використовує cookie та локальне сховище, щоб:
(а) підтримувати ваш вхід у систему та вашу сесію (автентифікація через Firebase);
(б) запам'ятовувати обрану вами мову (українську чи англійську) та інші налаштування інтерфейсу;
(в) запам'ятовувати ваш вибір щодо cookie, щоб не запитувати повторно під час кожного відвідування;
(г) забезпечувати безпеку Платформи та виявляти зловживання;
(ґ) розуміти в узагальненому та знеособленому вигляді, як використовується Платформа, щоб покращувати її.`,
  },
  {
    title: "3. Категорії cookie, які ми використовуємо",
    body: `Строго необхідні cookie. Потрібні для роботи Платформи — наприклад, щоб виконати вхід, підтримувати активну сесію та запам'ятати ваш вибір згоди. Їх не можна вимкнути через банер, оскільки без них Платформа не працюватиме.

Cookie налаштувань. Запам'ятовують ваш вибір, наприклад мову, щоб зробити взаємодію більш персоналізованою.

Аналітичні cookie. Допомагають нам зрозуміти, як відвідувачі взаємодіють із Платформою, збираючи інформацію в узагальненому та знеособленому вигляді. Встановлюються лише за вашої згоди.

Ми не використовуємо cookie для продажу ваших персональних даних або для показу реклами третіх сторін.`,
  },
  {
    title: "4. Cookie третіх сторін",
    body: `Деякі cookie можуть встановлюватися сторонніми сервісами, які ми використовуємо для роботи Платформи, зокрема:
(а) Firebase (Google) — автентифікація та керування сесіями;
(б) наші платіжні сервіси — під час поповнення Гаманця або здійснення оплати;
(в) Telegram — лише якщо ви взаємодієте з нашим ботом верифікації.

Ці постачальники обробляють дані відповідно до власних політик конфіденційності, які Profio не контролює.`,
  },
  {
    title: "5. Ваш вибір та керування cookie",
    body: `Під час першого відвідування Платформи банер дозволяє прийняти всі cookie або відхилити необов'язкові. Строго необхідні cookie завжди активні, оскільки без них Платформа не може функціонувати.

Ви також можете керувати cookie через налаштування вашого браузера — більшість браузерів дозволяють переглядати, видаляти та блокувати cookie. Зверніть увагу: якщо заблокувати строго необхідні cookie, окремі функції Платформи (наприклад, вхід у систему) можуть перестати працювати.

Щоб змінити вже зроблений вибір, видаліть значення «profio:cookie-consent» у сховищі вашого браузера (або дані сайту для profioplatform.com) та перезавантажте сторінку — банер з'явиться знову.`,
  },
  {
    title: "6. Строк зберігання cookie",
    body: `Сесійні cookie видаляються після закриття браузера. Постійні cookie (наприклад, мова та вибір згоди) зберігаються до закінчення строку їх дії або до їх видалення вами. Вибір згоди зберігається, щоб ми не запитували вас повторно під час кожного відвідування.`,
  },
  {
    title: "7. Зміни до цієї Політики",
    body: `Ми можемо час від часу оновлювати цю Політику щодо cookie, щоб відобразити зміни у технологіях, які ми використовуємо, або у чинному законодавстві. Актуальна редакція завжди доступна за адресою profioplatform.com/cookies, а дата «останнього оновлення» нижче вказує, коли її було востаннє переглянуто.`,
  },
  {
    title: "8. Контакти",
    body: `Якщо у вас є запитання щодо цієї Політики щодо cookie або того, як ми використовуємо cookie, зв'яжіться з нами за адресою support@profioplatform.com. Інформацію про те, як ми обробляємо персональні дані загалом, наведено в нашій Політиці конфіденційності за адресою profioplatform.com/privacy.`,
  },
];

export default function CookiePolicy() {
  const { i18n } = useTranslation();
  const isUk = (i18n.resolvedLanguage || i18n.language || "en").toLowerCase().startsWith("uk");

  const sections = isUk ? SECTIONS_UK : SECTIONS_EN;
  const title = isUk ? "Політика щодо файлів cookie" : "Cookie Policy";
  const updated = isUk ? "Останнє оновлення: липень 2026 р." : "Last updated: July 2026";
  const intro = isUk
    ? "Ця Політика пояснює, що таке файли cookie, які cookie використовує платформа Profio та як ви можете керувати ними. Використовуючи Profio, ви погоджуєтесь із використанням cookie відповідно до цієї Політики та вашого вибору в банері згоди."
    : "This Policy explains what cookies are, which cookies the Profio platform uses, and how you can manage them. By using Profio, you agree to the use of cookies in accordance with this Policy and the choice you make in the consent banner.";

  return (
    <div className="min-h-screen bg-[#272c39] text-white">
      <div className="mx-auto max-w-3xl px-4 md:px-6 pt-16 pb-28 md:pb-16">
        <h1 className="font-unbounded text-3xl font-bold">{title}</h1>
        <p className="mt-2 text-sm text-white/50">{updated}</p>
        <p className="mt-6 text-white/70 leading-relaxed">{intro}</p>

        <div className="mt-10 space-y-8">
          {sections.map((s) => (
            <section key={s.title}>
              <h2 className="font-semibold text-white text-base mb-2">{s.title}</h2>
              <p className="text-white/70 leading-relaxed text-sm whitespace-pre-line">{s.body}</p>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
